// Chord blocks: one slot per bar where you pick which chord the pad holds.
// Picking a chord writes its three notes into the pad lane (so they also
// show up in the piano roll); picking the active chord again clears the bar.
// With `variants`, each bar also gets sus2/sus4 buttons that swap the chord
// at the bar's current index for its sus version (or back to plain).

import React from "react";
import { cn } from "../../lib/utils";
import { CHORD_VARIANTS, chordInBar, diatonicChord } from "../../lib/starterTape";

export default function ChordStrip({
  song,
  trackId,
  chords,
  onPick,
  variants = false,
  onVariant,
}) {
  const options = chords.map((i) => diatonicChord(song, i)).filter(Boolean);
  const bars = Array.from({ length: song.bars }, (_, i) => i);

  return (
    <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-zinc-100">Chord blocks</p>
        <p className="text-xs text-zinc-500">
          Pick a chord for each bar. The pad holds it for the whole bar.
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
        {bars.map((bar) => {
          const current = chordInBar(song, trackId, bar);
          return (
            <div key={bar} className="rounded-lg bg-zinc-950/60 p-1.5">
              <p className="mb-1 text-center text-[10px] uppercase tracking-wide text-zinc-500">
                bar {bar + 1}
              </p>
              <div className="flex flex-col gap-1">
                {options.map((chord) => {
                  const active = current && current.index === chord.index;
                  const activeQuality = active ? current.quality : chord.quality;
                  return (
                    <button
                      key={chord.index}
                      type="button"
                      onClick={() => onPick(bar, active ? null : chord.index)}
                      aria-pressed={!!active}
                      aria-label={`Bar ${bar + 1}: ${chord.name}`}
                      className={cn(
                        "rounded-md px-1 py-1 text-sm",
                        active
                          ? activeQuality === "sus"
                            ? "bg-sky-300 text-zinc-900"
                            : activeQuality === "major"
                              ? "bg-amber-300 text-zinc-900"
                              : "bg-violet-400 text-zinc-900"
                          : "text-zinc-400 hover:bg-zinc-800"
                      )}
                    >
                      <span className="font-semibold">{chord.name}</span>
                      <span className="ml-1 text-xs opacity-70">
                        {chord.roman}
                      </span>
                    </button>
                  );
                })}
              </div>
              {variants && (
                <div className="mt-1 flex gap-1">
                  {CHORD_VARIANTS.filter(Boolean).map((v) => {
                    const on = !!current && current.variant === v;
                    const available =
                      !!current && !!diatonicChord(song, current.index, undefined, v);
                    return (
                      <button
                        key={v}
                        type="button"
                        disabled={!available}
                        onClick={() => onVariant(bar, on ? null : v)}
                        aria-pressed={on}
                        aria-label={`Bar ${bar + 1}: ${v}`}
                        className={cn(
                          "flex-1 rounded-md px-1 py-0.5 text-[10px] uppercase tracking-wide",
                          on
                            ? "bg-sky-300 text-zinc-900"
                            : available
                              ? "text-zinc-500 hover:bg-zinc-800"
                              : "text-zinc-700"
                        )}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
